import {
  Dropdown,
  IDropdownOption,
  IDropdownProps,
  INavLink,
  INavLinkGroup,
  IStackTokens,
  Icon,
  IconButton,
  Label,
  Nav,
  Stack,
  Text,
} from "@fluentui/react";
import classNames from "classnames";
import React from "react";
import styles from "../../styles/stationSelector.module.css";
import { AddStation } from "./AddStation";

export interface StationSelectorViewProps {
  regionOptions: IDropdownOption[];
  cafeOptions: IDropdownOption[];
  stationOptions: INavLink[];
  onRegionSelect: (option?: IDropdownOption) => void;
  onCafeSelect: (option?: IDropdownOption) => void;
  onStationClick: (link: INavLink) => void;
  onStationDeleteClick: () => void;
  selectedRegionId?: string;
  selectedCafeId?: string;
  selectedStation?: string;
}

const StationSelectorView = ({
  regionOptions,
  cafeOptions,
  stationOptions,
  onRegionSelect,
  onCafeSelect,
  onStationClick,
  onStationDeleteClick,
  selectedRegionId,
  selectedCafeId,
  selectedStation,
}: StationSelectorViewProps) => {
  // Nav group Header styling (Required in order to match styling of Dropdown)
  function _onRenderGroupHeader(group: INavLinkGroup): JSX.Element {
    return <label className="ms-fontSize-14 ms-fontWeight-semibold">{group.name}</label>;
  }

  function _onRenderLink(props: INavLink): JSX.Element {
    const isSelected: boolean = selectedStation === props.key;
    return (
      <Stack className={classNames(styles.stationLinkContainer, "ms-depth-4")}>
        <Stack horizontal={true} horizontalAlign={"space-between"} verticalAlign={"center"} wrap={true}>
          <Stack horizontal={true} horizontalAlign={"start"} grow={true} tokens={{ childrenGap: 5 }}>
            <Icon iconName={isSelected ? "CaretSolidRight" : ""} className={classNames(styles.arrowIcon)} />
            <Text
              className={isSelected ? styles.selectedStationLink : styles.stationLink}
              style={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                maxWidth: "250px",
              }}
            >
              {props.name}
            </Text>
          </Stack>
          {isSelected && (
            <IconButton
              aria-label={props.name + " Remove button"}
              iconProps={{ iconName: "CalculatorMultiply" }}
              onClick={onStationDeleteClick}
            />
          )}
        </Stack>
      </Stack>
    );
  }

  // Reformats stationOptions into the format required by Nav objects
  const formattedStationOptions: INavLinkGroup[] = [{ name: "Choose a Station", links: stationOptions }];

  // This determines how the Stack option displays the items within it
  const innerStackTokens: IStackTokens = {
    childrenGap: "10 0",
    maxWidth: "100%",
  };
  const OuterStackTokens: IStackTokens = {
    childrenGap: "35 0",
    maxWidth: "100%",
    padding: 15,
  };

  const onRenderTopLabel = (props: IDropdownProps): JSX.Element => {
    /* eslint-disable react/prop-types */
    return <Label className={styles.noTopPadding}>{props.label}</Label>;
  };

  return (
    <Stack horizontalAlign="stretch" verticalAlign="start" tokens={OuterStackTokens} verticalFill={true} grow={true}>
      <Stack.Item grow disableShrink tokens={innerStackTokens}>
        <Dropdown
          label="Choose a Region"
          placeholder="Choose a region..."
          options={regionOptions}
          defaultSelectedKey={selectedRegionId}
          onRenderLabel={onRenderTopLabel}
          disabled={!regionOptions || !regionOptions.length}
          onChange={(_e, option) => onRegionSelect(option)}
          data-testid="regionDropdown"
        />
        <Dropdown
          label="Choose a Cafe"
          placeholder="Choose a cafe..."
          options={cafeOptions}
          notifyOnReselect={true}
          defaultSelectedKey={selectedCafeId}
          disabled={!cafeOptions || !cafeOptions.length}
          onChange={(_e, option) => onCafeSelect(option)}
          data-testid="cafeDropdown"
        />
      </Stack.Item>
      <Stack.Item grow>
        {stationOptions != undefined && stationOptions.length > 0 && (
          <Nav
            onRenderGroupHeader={_onRenderGroupHeader}
            ariaLabel="Choose a station:"
            groups={formattedStationOptions}
            selectedKey={selectedStation || ""}
            onLinkClick={(_e, item) => (item?.key ? onStationClick(item) : null)}
            data-testid="stationNav"
            onRenderLink={_onRenderLink}
            className={styles.stationNav}
            //providing specfic styling to the link text to using INavStyles
            styles={{
              // these link styles override the default truncation behavior
              link: {
                whiteSpace: "normal",
                lineHeight: "100%",
                overflow: "visible",
                overflowWrap: "break-word",
                height: "inherit",
                minHeight: "inherit",
                minWidth: "inherit",
              },
              compositeLink: {
                minHeight: "inherit",
              },
              navItem: {
                minHeight: "44px",
              },
              navItems: {
                height: "50vh",
                overflowY: "auto",
                scrollbarWidth: "none",
                marginBottom: "0px",
              },
            }}
          />
        )}
      </Stack.Item>
      <Stack.Item grow disableShrink>
        {selectedCafeId && <AddStation />}
      </Stack.Item>
    </Stack>
  );
};

export default StationSelectorView;
